/**
 * Speurtocht Steps Management Screen
 * View and manage steps for a speurtocht
 */

import React, { useState, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Alert,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams, useFocusEffect } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import {
  useSpeurttochtAdmin,
  Speurtocht,
  SpeurttochtStep,
  STEP_TYPE_CONFIG,
  STEP_TYPE_COLORS,
} from "@/modules/speurtocht";

export default function StepsScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const colors = useThemeColors();
  const { fetchSpeurtocht, fetchSteps, deleteStep, reorderSteps, isSubmitting } =
    useSpeurttochtAdmin();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [speurtocht, setSpeurtocht] = useState<Speurtocht | null>(null);
  const [steps, setSteps] = useState<SpeurttochtStep[]>([]);

  /**
   * Load data
   */
  const loadData = useCallback(async () => {
    if (!id) return;

    const [speurttochtData, stepsData] = await Promise.all([
      fetchSpeurtocht(id),
      fetchSteps(id),
    ]);

    setSpeurtocht(speurttochtData);
    setSteps(stepsData);
    setIsLoading(false);
  }, [id, fetchSpeurtocht, fetchSteps]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
  }, [loadData]);

  /**
   * Handle delete step
   */
  const handleDeleteStep = (step: SpeurttochtStep) => {
    Alert.alert(
      "Stap Verwijderen",
      `Weet je zeker dat je "${step.title}" wilt verwijderen?`,
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            const success = await deleteStep(step.id);
            if (success) {
              setSteps((prev) => prev.filter((s) => s.id !== step.id));
            }
          },
        },
      ]
    );
  };

  /**
   * Move step up or down
   */
  const handleMoveStep = async (step: SpeurttochtStep, direction: "up" | "down") => {
    const currentIndex = steps.findIndex((s) => s.id === step.id);
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= steps.length) return;

    // Create new order
    const newSteps = [...steps];
    [newSteps[currentIndex], newSteps[newIndex]] = [
      newSteps[newIndex],
      newSteps[currentIndex],
    ];

    // Update local state immediately for responsiveness
    setSteps(newSteps);

    // Update order indices
    const stepOrders = newSteps.map((s, i) => ({ stepId: s.id, orderIndex: i }));
    await reorderSteps(stepOrders);
  };

  // Refresh data when screen comes into focus (e.g., after adding a step)
  useFocusEffect(
    useCallback(() => {
      loadData();
    }, [loadData])
  );

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <ActivityIndicator size="large" color="#10B981" />
      </SafeAreaView>
    );
  }

  if (!speurtocht) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <Text style={{ color: colors.text }}>Speurtocht niet gevonden</Text>
        <TouchableOpacity
          onPress={() => router.back()}
          className="mt-4 px-4 py-2 rounded-lg"
          style={{ backgroundColor: colors.surface }}
        >
          <Text style={{ color: colors.primary }}>Terug</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <View className="px-4 py-4 flex-row items-center justify-between">
        <View className="flex-row items-center flex-1">
          <TouchableOpacity
            onPress={() => router.back()}
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: colors.surface }}
            accessibilityRole="button"
            accessibilityLabel="Terug"
          >
            <Ionicons name="arrow-back" size={22} color={colors.text} />
          </TouchableOpacity>
          <View className="flex-1">
            <Text className="text-xl font-bold" style={{ color: colors.text }} numberOfLines={1}>
              {speurtocht.name}
            </Text>
            <Text className="text-sm" style={{ color: colors.textSecondary }}>
              {steps.length} stap{steps.length !== 1 ? "pen" : ""}
            </Text>
          </View>
        </View>

        <View className="flex-row">
          {/* Settings */}
          <TouchableOpacity
            onPress={() => router.push(`/(admin)/games/speurtocht/${id}`)}
            className="w-10 h-10 rounded-full items-center justify-center mr-2"
            style={{ backgroundColor: colors.surface }}
            accessibilityRole="button"
            accessibilityLabel="Instellingen"
          >
            <Ionicons name="settings-outline" size={20} color={colors.text} />
          </TouchableOpacity>

          {/* Add Step */}
          <TouchableOpacity
            onPress={() => router.push(`/(admin)/games/speurtocht/${id}/add-step`)}
            className="w-10 h-10 rounded-full items-center justify-center"
            style={{ backgroundColor: "#10B981" }}
            accessibilityRole="button"
            accessibilityLabel="Stap toevoegen"
          >
            <Ionicons name="add" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Status Banner */}
      {speurtocht.status !== "draft" && (
        <View
          className="mx-4 mb-4 p-3 rounded-xl flex-row items-center"
          style={{
            backgroundColor:
              speurtocht.status === "active" ? "#10B98120" : "#F59E0B20",
          }}
        >
          <Ionicons
            name={speurtocht.status === "active" ? "play-circle" : "pause-circle"}
            size={20}
            color={speurtocht.status === "active" ? "#10B981" : "#F59E0B"}
          />
          <Text
            className="ml-2 flex-1"
            style={{
              color: speurtocht.status === "active" ? "#10B981" : "#F59E0B",
            }}
          >
            {speurtocht.status === "active"
              ? "Deze speurtocht is actief"
              : `Status: ${speurtocht.status}`}
          </Text>
          {speurtocht.status === "active" && (
            <TouchableOpacity
              onPress={() => router.push(`/(admin)/games/speurtocht/${id}/progress`)}
              className="px-3 py-1 rounded-lg"
              style={{ backgroundColor: "#10B981" }}
            >
              <Text className="text-white font-medium text-sm">Voortgang</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {steps.length === 0 ? (
          <View className="py-12 items-center px-4">
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: "#10B98120" }}
            >
              <Ionicons name="footsteps" size={40} color="#10B981" />
            </View>
            <Text
              className="text-lg font-semibold mb-2"
              style={{ color: colors.text }}
            >
              Geen stappen
            </Text>
            <Text
              className="text-center mb-6"
              style={{ color: colors.textSecondary }}
            >
              Voeg stappen toe om de speurtocht te bouwen
            </Text>
            <TouchableOpacity
              onPress={() => router.push(`/(admin)/games/speurtocht/${id}/add-step`)}
              className="px-6 py-3 rounded-xl flex-row items-center"
              style={{ backgroundColor: "#10B981" }}
            >
              <Ionicons name="add" size={20} color="#FFFFFF" />
              <Text className="ml-2 font-bold text-white">Eerste Stap</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View className="px-4 pb-4">
            {/* Points Summary */}
            <View
              className="rounded-xl p-4 mb-4"
              style={{ backgroundColor: colors.surface }}
            >
              <Text
                className="text-sm font-medium mb-2"
                style={{ color: colors.textSecondary }}
              >
                Punten overzicht
              </Text>
              <View className="flex-row justify-between">
                <View>
                  <Text className="text-2xl font-bold" style={{ color: colors.text }}>
                    {steps.length * speurtocht.points_per_step}
                  </Text>
                  <Text className="text-xs" style={{ color: colors.textSecondary }}>
                    Stappen ({steps.length} × {speurtocht.points_per_step})
                  </Text>
                </View>
                <View>
                  <Text className="text-2xl font-bold" style={{ color: "#10B981" }}>
                    +{speurtocht.completion_bonus}
                  </Text>
                  <Text className="text-xs" style={{ color: colors.textSecondary }}>
                    Voltooiing
                  </Text>
                </View>
                <View>
                  <Text className="text-2xl font-bold" style={{ color: "#F59E0B" }}>
                    +{speurtocht.first_team_bonus}
                  </Text>
                  <Text className="text-xs" style={{ color: colors.textSecondary }}>
                    Eerste team
                  </Text>
                </View>
              </View>
            </View>

            {/* Steps List */}
            {steps.map((step, index) => {
              const stepConfig = STEP_TYPE_CONFIG[step.step_type];
              const stepColor = STEP_TYPE_COLORS[step.step_type];

              return (
                <View
                  key={step.id}
                  className="rounded-xl mb-3 overflow-hidden"
                  style={{
                    backgroundColor: colors.surface,
                    borderLeftWidth: 4,
                    borderLeftColor: stepColor,
                  }}
                >
                  <TouchableOpacity
                    onPress={() =>
                      router.push(`/(admin)/games/speurtocht/${id}/step/${step.id}`)
                    }
                    activeOpacity={0.7}
                    className="p-4"
                  >
                    <View className="flex-row items-start">
                      {/* Step Number */}
                      <View
                        className="w-8 h-8 rounded-full items-center justify-center mr-3"
                        style={{ backgroundColor: `${stepColor}25` }}
                      >
                        <Text
                          className="font-bold"
                          style={{ color: stepColor }}
                        >
                          {index + 1}
                        </Text>
                      </View>

                      {/* Step Info */}
                      <View className="flex-1">
                        <Text
                          className="font-bold text-base"
                          style={{ color: colors.text }}
                        >
                          {step.title}
                        </Text>
                        {step.description && (
                          <Text
                            className="text-sm mt-0.5"
                            style={{ color: colors.textSecondary }}
                            numberOfLines={2}
                          >
                            {step.description}
                          </Text>
                        )}

                        {/* Type Badge */}
                        <View className="flex-row items-center mt-2">
                          <View
                            className="flex-row items-center px-2 py-1 rounded"
                            style={{ backgroundColor: `${stepColor}20` }}
                          >
                            <Ionicons
                              name={stepConfig.icon as any}
                              size={12}
                              color={stepColor}
                            />
                            <Text
                              className="text-xs ml-1 font-medium"
                              style={{ color: stepColor }}
                            >
                              {stepConfig.label}
                            </Text>
                          </View>
                        </View>
                      </View>

                      {/* Actions */}
                      <View className="items-center">
                        {/* Move Up */}
                        <TouchableOpacity
                          onPress={() => handleMoveStep(step, "up")}
                          disabled={index === 0}
                          className="w-8 h-8 rounded-full items-center justify-center mb-1"
                          style={{
                            backgroundColor: colors.surfaceSecondary,
                            opacity: index === 0 ? 0.3 : 1,
                          }}
                        >
                          <Ionicons
                            name="chevron-up"
                            size={16}
                            color={colors.textSecondary}
                          />
                        </TouchableOpacity>

                        {/* Move Down */}
                        <TouchableOpacity
                          onPress={() => handleMoveStep(step, "down")}
                          disabled={index === steps.length - 1}
                          className="w-8 h-8 rounded-full items-center justify-center mb-1"
                          style={{
                            backgroundColor: colors.surfaceSecondary,
                            opacity: index === steps.length - 1 ? 0.3 : 1,
                          }}
                        >
                          <Ionicons
                            name="chevron-down"
                            size={16}
                            color={colors.textSecondary}
                          />
                        </TouchableOpacity>

                        {/* Delete */}
                        <TouchableOpacity
                          onPress={() => handleDeleteStep(step)}
                          className="w-8 h-8 rounded-full items-center justify-center"
                          style={{ backgroundColor: colors.error + "20" }}
                        >
                          <Ionicons
                            name="trash"
                            size={14}
                            color={colors.error}
                          />
                        </TouchableOpacity>
                      </View>
                    </View>
                  </TouchableOpacity>
                </View>
              );
            })}
          </View>
        )}

        <View className="h-24" />
      </ScrollView>

      {/* Floating Add Button */}
      {steps.length > 0 && (
        <TouchableOpacity
          onPress={() => router.push(`/(admin)/games/speurtocht/${id}/add-step`)}
          className="absolute bottom-6 right-6 w-14 h-14 rounded-full items-center justify-center shadow-lg"
          style={{
            backgroundColor: "#10B981",
            shadowColor: "#10B981",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 8,
          }}
          accessibilityRole="button"
          accessibilityLabel="Stap toevoegen"
        >
          <Ionicons name="add" size={28} color="#FFFFFF" />
        </TouchableOpacity>
      )}
    </SafeAreaView>
  );
}

